/* eslint-disable react-hooks/rules-of-hooks */
import PropTypes from 'prop-types';
import React, {memo, useCallback, useMemo} from 'react';
import {Button, Form, Popconfirm} from 'antd';
import Tooltip from '@/components/Tooltip';
import {useTranslation} from 'react-i18next';
import FloatContainer from '@/components/FloatContainer';
import useCrud from '@/hooks/useCrud';
import {useHistory} from 'react-router-dom';
import {RouteLoader} from '@dofleini/security';
import FormSkeleton from '@/components/FormGenerator/FormSkeleton';
import {useFormTitle} from '@/hooks/useFormTitle';
import OpenDetail from '@/components/OpenDialog';
import {DeleteOutlined, FormOutlined} from '@ant-design/icons';
import {EntityDetailContent} from '@/components/EntityDetailContent';
import isEmpty from 'lodash/isEmpty';
import FormBuilder from 'antd-form-builder';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {DetailsContextProvider, useDetailsContext} from '@/contexts/DetailsContext';
import {createReferenceWarning} from '@/utils/createReferenceWarning';
import LanguageSelect from '@/components/LanguageSelect';
import {createTranslatedPayload} from '@/utils/createTranslatedPayload';
import compose from 'lodash/fp/compose';
import {InternationalizationProvider} from '@/contexts/InternationalizationContext';
import {isActiveMultiLanguageByFields} from '@/utils/mountedLanguages';

const PopConfirmDelete = (props) => {
  return <Popconfirm {...props}/>;
};

const headerActions = ({headerProps = {}, data, t, viewMode, onSubmit, toggleView, handleClose, onDelete, isLoading, activeMultiLanguage, ConfirmDelete=PopConfirmDelete}) => {
  const {extras, canRemove, canEdit, extrasEdit, hideSaveEdit, hideCancelEdit} = headerProps;
  const ExtraOptions = extras;
  const ExtraOptionsEdit = extrasEdit;
  if (viewMode)
    return [
      activeMultiLanguage && <LanguageSelect key={'lang'}/>,
      ExtraOptions && <ExtraOptions key={'detail-extra-actions'} data={data}/>,
      canEdit && <Tooltip parentContainer title={t('edit')} key={'edit'}>
        <Button type="text" icon={<FormOutlined/>} onClick={toggleView} disabled={isLoading}/>
      </Tooltip>,
      canRemove && <ConfirmDelete
        key={'remove'}
        placement="bottomRight"
        title={t('deleteConfirmation')}
        onConfirm={onDelete}
        okText={t('delete')}
        cancelText={t('cancel')}
        data={data}
      >
        <Tooltip parentContainer title={t('delete')}>
          <Button type="text" icon={<DeleteOutlined/>} loading={isLoading}/>
        </Tooltip>
      </ConfirmDelete>
    ];
  return (
    [
      activeMultiLanguage && <LanguageSelect key={'lang'}/>,
      ExtraOptionsEdit && <ExtraOptionsEdit
        data={data}
        onSubmit={onSubmit}
        isLoading={isLoading}
        onCancel={handleClose}/>,

      !hideSaveEdit && <Button
        key="submit"
        type="primary"
        onClick={onSubmit}
        loading={isLoading}
      >
        {t('save')}
      </Button>,
      !hideCancelEdit && <Button key="back" onClick={handleClose} disabled={isLoading}>
        {t('cancel')}
      </Button>,
    ]
  );
};

export const createEntityDetail = (
  {
    route, service, name, module, translation, translationPrefix, useFields, useGetOne,
    options = {
      create: true,
      edit: true,
      details: true
    },
    willForceUpdate,
    getOneKey = 'getOne',
    mapPayload = v => v,
    mapResponse = v => v,
    messageReferenceWarningDialog,
    fields,
    ConfirmDelete,
    ...props
  }) => {

  const activeMultiLanguage = isActiveMultiLanguageByFields(fields);

  const translatedPayload = createTranslatedPayload(fields, !activeMultiLanguage);

  const {
    useErrorMiddleware,
    ReferenceWarningDialog,
  } = createReferenceWarning({
    translation,
    service,
    module,
    message: messageReferenceWarningDialog,
  });

  // eslint-disable-next-line react/display-name
  const FormComponent = memo(({headerProps}) => {
    const {t} = useTranslation(translation);
    const {push} = useHistory();
    const {create, update, remove} = useCrud(service, module, getOneKey);
    const {isOpen, closeDialog, payload} = useDetailsContext(name);
    const errorHandler = useErrorMiddleware();
    const {isLoading, data: res} = useGetOne(payload);
    const [form] = Form.useForm();
    const {title, viewMode, toggleView, backToView} = useFormTitle();
    const fields = useFields(viewMode, form);
    const forceUpdate = FormBuilder.useForceUpdate();

    const data = useMemo(() => mapResponse(res?.data), [res]);

    const initValues = data || {};

    const id = initValues?._id;

    const close = useCallback(() => {
      closeDialog();
      setTimeout(() => {
        route && push(route);
        form.resetFields();
      }, 500);
    }, [closeDialog, form, push]);

    const onClose = useCallback(() => {
      if (backToView) {
        return toggleView();
      }
      close();
    }, [backToView, close, toggleView]);

    const handleFinish = useCallback(async (values) => {
      if (id) {
        const {data} = await update({_id: id, ...values});
        form.setFieldsValue(mapResponse(data));
      } else
        await create(values);
      onClose();
    }, [id, create, onClose, update, form]);

    const onFinish = useCallback(() => {
      const values = compose(
        mapPayload,
        translatedPayload,
      )(form.getFieldsValue(true) || {});

      handleFinish(values).catch(
        errorHandler({
          onForce: () => handleFinish({force: true, ...values}),
          onRecovery: () => close()
        })
      );
    }, [close, errorHandler, form, handleFinish]);

    const handleDelete = useCallback(async () => {
      if (initValues) {
        await remove(initValues?._id);
        onClose();
      }
    }, [onClose, initValues, remove]);

    const handleClose = useCallback(() => {
      form.setFieldsValue(initValues);
      onClose();
    }, [form, initValues, onClose]);

    useDeepCompareEffect(() => {
      if (!isEmpty(initValues)) {
        form.setFieldsValue(initValues);
      }
    }, [initValues]);

    return (
      <FloatContainer
        {...props}
        visible={isOpen}
        title={t(`${translationPrefix}.${title}`)}
        onClose={onClose}
        footer={headerActions({
          headerProps,
          isLoading,
          handleClose,
          data: initValues,
          onDelete: handleDelete,
          onSubmit: () => form.submit(),
          t,
          toggleView,
          viewMode,
          activeMultiLanguage,
          ConfirmDelete
        })}>
        <Form
          form={form} onFinish={onFinish} layout={!viewMode ? 'vertical' : 'horizontal'}
          onValuesChange={willForceUpdate ? forceUpdate : false}>
          {isLoading ?
            <FormSkeleton/> :
            <EntityDetailContent viewMode={viewMode} form={form} data={initValues} fields={fields}/>
          }
        </Form>
      </FloatContainer>
    );
  });

  const pathRoute = [];
  if (options?.create) {
    route && pathRoute.push(`${route}/create`);
  }
  if (options?.details) {
    route && pathRoute.push(`${route}/:id`);
  }
  if (options?.edit) {
    route && pathRoute.push(`${route}/:id/edit`);
  }

  const routes = {
    NestedView: {
      path: pathRoute,
      exact: true,
      data: {
        dialog: name
      },
      component: OpenDetail
    }
  };

  // eslint-disable-next-line react/display-name
  const Detail = memo((props) => {
    return <>
      <InternationalizationProvider>
        <DetailsContextProvider>
          <FormComponent {...props}/>
          <RouteLoader routes={routes} notfoundRedirect={route}/>
          <ReferenceWarningDialog/>
        </DetailsContextProvider>
      </InternationalizationProvider>
    </>;
  });
  FormComponent.propTypes = {
    headerProps: PropTypes.object
  };
  FormComponent.defaultProps = {
    headerProps: {canRemove: true, canEdit: true}
  };
  return {FormComponent, Detail};
};


createEntityDetail.propTypes = {
  module: PropTypes.any,
  name: PropTypes.any,
  options: PropTypes.object,
  route: PropTypes.any,
  service: PropTypes.any,
  translation: PropTypes.any,
  translationPrefix: PropTypes.any,
  useFields: PropTypes.any,
  useGetOne: PropTypes.any,
  willForceUpdate: PropTypes.bool,
  mapPayload: PropTypes.func,
};
